/**
 * PLAN-PRICING-ADMIN — Settings → Plan Pricing. The office raises PLUS-plan
 * prices here without code edits (Seth: "editable / changeable for pricing
 * increases by the office"). These rows ARE what the PlanBuilder reads, so a
 * change here is the very next quote's number.
 *
 * Three groups — Base plans, Add-ons, Items — each a row you edit inline and
 * save when it's dirty, plus add-a-row and archive. The two plan-wide knobs
 * (trip charge, 2nd-system discount) sit up top. Repair discount is NOT edited
 * here — it lives once in Pricing → Membership Repair Discount and the builder
 * derives the benefit from those tiers (one source of truth).
 *
 * Writes gated on settings.manage; read-only otherwise.
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Plus,
  DollarSign,
  Percent,
  Archive,
  RotateCcw,
  TrendingUp,
  ChevronRight,
  Check,
  X,
  Wrench,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { InfoTip } from "@/components/InfoTip";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { usePlanDiscounts } from "@/features/customer-360/serviceTypes";
import {
  usePlanPriceCatalog,
  useSavePlanPrice,
  useAddPlanPrice,
  usePlanConfig,
  useSavePlanConfig,
  useBumpPlanPrices,
  planRound,
  type PlanKind,
  type RoundMode,
} from "@/features/agreements/planPricing";
import type { PlanPrice } from "@/features/agreements/PlanBuilder";
import { DEFAULT_TUNEUP_TEMPLATE, type TuneupTemplateItem } from "@/features/agreements/obligations";

const MONTHS = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const TUNEUP_CATS = ["heating", "cooling", "plumbing", "electrical", "other"];

const TERMS = [1, 2, 3, 5, 10] as const;
type TermN = (typeof TERMS)[number];
const TERM_FIELD: Record<TermN, "p1" | "p2" | "p3" | "p5" | "p10"> = {
  1: "p1", 2: "p2", 3: "p3", 5: "p5", 10: "p10",
};
const GROUPS: { kind: PlanKind; title: string; blurb: string }[] = [
  { kind: "base", title: "Base Plans", blurb: "The equipment package the customer is covered on; each plan picks one. Priced by term." },
  { kind: "addon", title: "Add-ons", blurb: "Accessories added to the base plan, like filters, air exchangers and IAQ kits. Priced by term." },
  { kind: "item", title: "Items", blurb: "One-time items, like bulbs, filter cases or a garage heater. One flat price." },
];

export function PlanPricingSection() {
  const canManage = useCan("settings.manage");
  const { data: rows, isLoading } = usePlanPriceCatalog();

  const byKind = useMemo(() => {
    const m = new Map<PlanKind, PlanPrice[]>();
    for (const r of rows ?? []) m.set(r.kind, [...(m.get(r.kind) ?? []), r]);
    return m;
  }, [rows]);

  return (
    <div className="space-y-4">

      <PlanConfigCard canManage={canManage} />

      {canManage && <GlobalIncreaseCard rows={rows ?? []} />}

      {isLoading ? (
        <div className="rounded-xl border border-hairline bg-white p-6 text-center text-sm text-slate-400 shadow-e1">
          Loading price book…
        </div>
      ) : (
        GROUPS.map((g) => (
          <PlanGroupCard
            key={g.kind}
            group={g}
            rows={byKind.get(g.kind) ?? []}
            canManage={canManage}
          />
        ))
      )}

      <RepairDiscountNote />
    </div>
  );
}

// ── Plan-wide knobs ─────────────────────────────────────────────────────────
function PlanConfigCard({ canManage }: { canManage: boolean }) {
  const { data: cfg } = usePlanConfig();
  if (!cfg) return null;
  const trip = Number(cfg.trip_charge ?? 79);
  const secondPct = Math.round(Number(cfg.second_system_discount ?? 0.3) * 100);
  return (
    <div className="rounded-xl border border-hairline bg-white p-4 shadow-e1">
      <div className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
        Plan-wide settings
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <ConfigNumber
          icon={<DollarSign className="h-3.5 w-3.5 text-slate-400" />}
          label="Trip charge"
          hint="The diagnostic/visit fee a member pays — shown as a benefit on the plan."
          configKey="trip_charge"
          value={trip}
          unit="$"
          unitSide="left"
          canManage={canManage}
        />
        <ConfigNumber
          icon={<Percent className="h-3.5 w-3.5 text-slate-400" />}
          label="2nd system discount"
          hint="Discount applied to a second system added to the same plan."
          configKey="second_system_discount"
          value={secondPct}
          unit="%"
          unitSide="right"
          asFraction
          canManage={canManage}
        />
      </div>
    </div>
  );
}

function ConfigNumber({
  icon, label, hint, configKey, value, unit, unitSide, asFraction, canManage,
}: {
  icon: ReactNode; label: string; hint: string; configKey: string;
  value: number; unit: string; unitSide: "left" | "right"; asFraction?: boolean;
  canManage: boolean;
}) {
  const save = useSavePlanConfig();
  const [draft, setDraft] = useState(String(value));
  const [flash, setFlash] = useState(false);
  const dirty = draft !== String(value);
  const parsed = Number(draft);
  const valid = Number.isFinite(parsed) && parsed >= 0;

  const commit = () =>
    save.mutate(
      { key: configKey, value: asFraction ? parsed / 100 : parsed },
      {
        onSuccess: () => {
          setFlash(true);
          window.setTimeout(() => setFlash(false), 2200);
        },
      },
    );

  return (
    <div>
      <div className="flex items-center gap-1.5 text-sm font-medium text-slate-800">
        {icon} {label}
        <InfoTip label={`About ${label.toLowerCase()}`}>{hint}</InfoTip>
      </div>
      <div className="mt-1.5 flex items-center gap-1.5">
        {unitSide === "left" && <span className="text-slate-400">{unit}</span>}
        <input
          type="number"
          min={0}
          value={draft}
          disabled={!canManage}
          onChange={(e) => setDraft(e.target.value)}
          className="h-8 w-24 rounded-md border border-hairline px-2 text-right text-sm font-semibold text-slate-800 disabled:bg-slate-50"
        />
        {unitSide === "right" && <span className="text-slate-400">{unit}</span>}
        {canManage && dirty && (
          <button
            type="button"
            disabled={!valid || save.isPending}
            onClick={commit}
            className="rounded-md bg-brand-700 px-2.5 py-1.5 text-[11px] font-semibold text-white hover:bg-brand-800 disabled:bg-brand-300"
          >
            Save
          </button>
        )}
        {flash && (
          <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-700">
            saved ✓
          </span>
        )}
      </div>
    </div>
  );
}

// ── Global price increase (the annual bump) ─────────────────────────────────
const SCOPES: { key: string; label: string; kinds: PlanKind[] }[] = [
  { key: "all", label: "Everything", kinds: ["base", "addon", "item"] },
  { key: "plans", label: "Plans & Add-Ons", kinds: ["base", "addon"] },
  { key: "base", label: "Base Plans Only", kinds: ["base"] },
  { key: "addon", label: "Add-Ons Only", kinds: ["addon"] },
  { key: "item", label: "Items Only", kinds: ["item"] },
];
const ROUND_OPTS: { key: RoundMode; label: string }[] = [
  { key: "exact", label: "Exact Cents" },
  { key: "dollar", label: "Nearest $1" },
  { key: "five", label: "Nearest $5" },
];
const fmtUsd = (v: number) => v.toLocaleString("en-US", { style: "currency", currency: "USD" });

function GlobalIncreaseCard({ rows }: { rows: PlanPrice[] }) {
  const bump = useBumpPlanPrices();
  const [open, setOpen] = useState(false);
  const [pctStr, setPctStr] = useState("5");
  const [scopeKey, setScopeKey] = useState("all");
  const [round, setRound] = useState<RoundMode>("exact");
  const [previewing, setPreviewing] = useState(false);
  const [doneFlash, setDoneFlash] = useState(false);

  const scope = SCOPES.find((s) => s.key === scopeKey) ?? SCOPES[0];
  const pct = Number(pctStr);
  const validPct = Number.isFinite(pct) && pct !== 0 && pct > -100;
  const f = 1 + pct / 100;

  // Representative figure per row (3-year for plans/add-ons, flat for items) —
  // every term moves the same %, so one column tells the story without a wall.
  const repr = (r: PlanPrice): number | null => (r.kind === "item" ? r.flat : r.p3);
  const affected = rows.filter((r) => scope.kinds.includes(r.kind) && repr(r) != null);

  const apply = () =>
    bump.mutate(
      { pct, kinds: scope.kinds, round },
      {
        onSuccess: () => {
          setPreviewing(false);
          setOpen(false);
          setDoneFlash(true);
          window.setTimeout(() => setDoneFlash(false), 2800);
        },
      },
    );

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-full items-center gap-2 rounded-xl border border-dashed border-slate-300 bg-white px-4 py-2.5 text-left text-sm font-medium text-slate-600 transition-colors hover:border-brand-300 hover:bg-brand-50/40"
      >
        <TrendingUp className="h-4 w-4 text-brand-600" />
        Raise all prices
        {doneFlash && (
          <span className="ml-auto rounded bg-emerald-50 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-700">
            prices updated ✓
          </span>
        )}
      </button>
    );
  }

  return (
    <div className="rounded-xl border border-brand-200 bg-white p-4 shadow-e1">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900">
        <TrendingUp className="h-4 w-4 text-brand-600" /> Raise all prices
      </div>

      {!previewing ? (
        <>
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-xs font-medium text-slate-500">
              Increase by
              <div className="mt-1 flex items-center gap-1">
                <input
                  type="number"
                  value={pctStr}
                  onChange={(e) => setPctStr(e.target.value)}
                  className="h-8 w-20 rounded-md border border-hairline px-2 text-right text-sm font-semibold text-slate-800 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
                />
                <span className="text-slate-400">%</span>
              </div>
            </label>
            <label className="text-xs font-medium text-slate-500">
              Applies to
              <select
                value={scopeKey}
                onChange={(e) => setScopeKey(e.target.value)}
                className="mt-1 block h-8 rounded-md border border-hairline px-2 text-sm text-slate-800"
              >
                {SCOPES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </label>
            <label className="text-xs font-medium text-slate-500">
              Round to
              <select
                value={round}
                onChange={(e) => setRound(e.target.value as RoundMode)}
                className="mt-1 block h-8 rounded-md border border-hairline px-2 text-sm text-slate-800"
              >
                {ROUND_OPTS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
            </label>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              disabled={!validPct || affected.length === 0}
              onClick={() => setPreviewing(true)}
              className="inline-flex items-center gap-1 rounded-md bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-800 disabled:bg-brand-300"
            >
              Preview <ChevronRight className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-md px-2.5 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-100"
            >
              Close
            </button>
            <span className="text-[11px] text-slate-400">Nothing changes until you confirm the preview.</span>
          </div>
        </>
      ) : (
        <>
          <div className="mb-2 flex items-center justify-between">
            <div className="text-xs text-slate-500">
              <span className="font-semibold text-slate-800">{affected.length} {affected.length === 1 ? "price" : "prices"}</span>
              {" · "}{pct > 0 ? "+" : ""}{pct}% · {ROUND_OPTS.find((r) => r.key === round)?.label.toLowerCase()}
              <InfoTip label="What the preview shows" className="ml-1.5">
                Every term rises the same percent. The list shows the 3-year price, or the flat price for items.
              </InfoTip>
            </div>
          </div>
          <ul className="max-h-56 divide-y divide-hairline overflow-y-auto rounded-lg border border-hairline">
            {affected.map((r) => {
              const old = repr(r) as number;
              const next = planRound(old * f, round) as number;
              return (
                <li key={r.id} className="flex items-center justify-between gap-3 px-3 py-1.5 text-sm">
                  <span className="min-w-0 truncate text-slate-700">{r.name}</span>
                  <span className="flex shrink-0 items-center gap-1.5 tabular-nums">
                    <span className="text-slate-400 line-through">{fmtUsd(old)}</span>
                    <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
                    <span className="font-semibold text-emerald-700">{fmtUsd(next)}</span>
                  </span>
                </li>
              );
            })}
          </ul>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              disabled={bump.isPending}
              onClick={apply}
              className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:bg-emerald-300"
            >
              <Check className="h-3.5 w-3.5" /> {bump.isPending ? "Applying…" : `Apply to ${affected.length}`}
            </button>
            <button
              type="button"
              onClick={() => setPreviewing(false)}
              className="inline-flex items-center gap-1 rounded-md border border-hairline px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
            >
              <X className="h-3.5 w-3.5" /> Back
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── A group of price rows (base / addon / item) ─────────────────────────────
function PlanGroupCard({
  group, rows, canManage,
}: {
  group: { kind: PlanKind; title: string; blurb: string };
  rows: PlanPrice[];
  canManage: boolean;
}) {
  const add = useAddPlanPrice();
  const isItem = group.kind === "item";
  const maxSort = rows.reduce((m, r) => Math.max(m, r.sort), 0);

  return (
    <div className="rounded-xl border border-hairline bg-white p-4 shadow-e1">
      <div className="mb-1 flex items-center justify-between gap-3">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          {group.title}
          <InfoTip label={`About ${group.title.toLowerCase()}`}>{group.blurb}</InfoTip>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => add.mutate({ kind: group.kind, name: "New " + (isItem ? "item" : "plan"), afterSort: maxSort })}
            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-dashed border-slate-300 px-2 py-1 text-[11px] font-medium text-slate-500 hover:bg-slate-50"
          >
            <Plus className="h-3 w-3" /> Add row
          </button>
        )}
      </div>

      {/* term header (base/addon only) */}
      {!isItem && (
        <div className="mt-3 flex items-center gap-2 border-b border-hairline pb-1 pl-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
          <span className="min-w-0 flex-1">Plan</span>
          {TERMS.map((t) => (
            <span key={t} className="w-[84px] pr-1.5 text-right tabular-nums">{t} yr</span>
          ))}
          <span className="w-[68px]" />
        </div>
      )}

      <ul className="divide-y divide-hairline">
        {rows.map((r) => (
          <PlanPriceRow key={r.id} row={r} isItem={isItem} canManage={canManage} showTuneups={group.kind === "base"} />
        ))}
        {rows.length === 0 && (
          <li className="py-3 text-center text-xs text-slate-400">
            No rows yet — add one to start.
          </li>
        )}
      </ul>
    </div>
  );
}

function PlanPriceRow({
  row, isItem, canManage, showTuneups,
}: {
  row: PlanPrice;
  isItem: boolean;
  canManage: boolean;
  showTuneups?: boolean;
}) {
  const save = useSavePlanPrice();
  const [name, setName] = useState(row.name);
  const [prices, setPrices] = useState<Record<string, string>>(() => ({
    p1: row.p1 == null ? "" : String(row.p1),
    p2: row.p2 == null ? "" : String(row.p2),
    p3: row.p3 == null ? "" : String(row.p3),
    p5: row.p5 == null ? "" : String(row.p5),
    p10: row.p10 == null ? "" : String(row.p10),
    flat: row.flat == null ? "" : String(row.flat),
  }));

  // SETTINGS-AUDIT-01: re-seed local inputs whenever the SERVER row changes
  // (e.g. after "Raise all prices"). Without this, the grid kept showing the
  // pre-increase numbers and every row grew a Save button that would have
  // written the OLD price back over the bump.
  useEffect(() => {
    setName(row.name);
    setPrices({
      p1: row.p1 == null ? "" : String(row.p1),
      p2: row.p2 == null ? "" : String(row.p2),
      p3: row.p3 == null ? "" : String(row.p3),
      p5: row.p5 == null ? "" : String(row.p5),
      p10: row.p10 == null ? "" : String(row.p10),
      flat: row.flat == null ? "" : String(row.flat),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.name, row.p1, row.p2, row.p3, row.p5, row.p10, row.flat]);

  const orig: Record<string, string> = {
    p1: row.p1 == null ? "" : String(row.p1),
    p2: row.p2 == null ? "" : String(row.p2),
    p3: row.p3 == null ? "" : String(row.p3),
    p5: row.p5 == null ? "" : String(row.p5),
    p10: row.p10 == null ? "" : String(row.p10),
    flat: row.flat == null ? "" : String(row.flat),
  };
  const dirty =
    name !== row.name ||
    (isItem ? prices.flat !== orig.flat : TERMS.some((t) => prices[TERM_FIELD[t]] !== orig[TERM_FIELD[t]]));

  const num = (s: string): number | null => (s.trim() === "" ? null : Number(s));

  const commit = () => {
    const base = { id: row.id, name: name.trim() || row.name };
    if (isItem) {
      save.mutate({ ...base, flat: num(prices.flat) });
    } else {
      save.mutate({
        ...base,
        p1: num(prices.p1), p2: num(prices.p2), p3: num(prices.p3),
        p5: num(prices.p5), p10: num(prices.p10),
      });
    }
  };

  const setPrice = (field: string, v: string) =>
    setPrices((m) => ({ ...m, [field]: v }));

  return (
    <li className={cn("py-2 pl-1", !row.active && "opacity-50")}>
      <div className="flex items-center gap-2">
      <input
        value={name}
        disabled={!canManage}
        onChange={(e) => setName(e.target.value)}
        className="min-w-0 flex-1 rounded-md border border-transparent bg-transparent px-1.5 py-1 text-sm text-slate-800 hover:border-hairline focus:border-brand-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-500/20 disabled:hover:border-transparent"
      />

      {isItem ? (
        <span className="flex items-center gap-1">
          <span className="text-[11px] text-slate-400">$</span>
          <input
            type="number"
            value={prices.flat}
            disabled={!canManage}
            onChange={(e) => setPrice("flat", e.target.value)}
            className="w-24 rounded-md border border-hairline px-2 py-1 text-right text-sm tabular-nums text-slate-800 disabled:bg-slate-50 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          />
        </span>
      ) : (
        TERMS.map((t) => (
          <input
            key={t}
            type="number"
            value={prices[TERM_FIELD[t]]}
            disabled={!canManage}
            onChange={(e) => setPrice(TERM_FIELD[t], e.target.value)}
            className="w-[84px] rounded-md border border-hairline px-1.5 py-1 text-right text-sm tabular-nums text-slate-800 disabled:bg-slate-50 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            aria-label={`${row.name} ${t} year price`}
          />
        ))
      )}

      <div className="flex w-[68px] shrink-0 items-center justify-end gap-0.5">
        {canManage && dirty && (
          <button
            type="button"
            onClick={commit}
            disabled={save.isPending}
            className="rounded-md bg-brand-700 px-2 py-1 text-[11px] font-semibold text-white hover:bg-brand-800 disabled:bg-brand-300"
          >
            Save
          </button>
        )}
        {canManage && !dirty && (
          <button
            type="button"
            onClick={() => save.mutate({ id: row.id, active: !row.active })}
            title={row.active ? "Archive — hides it from the plan builder" : "Restore"}
            className={cn(
              "rounded-md p-1 transition-colors",
              row.active
                ? "text-slate-300 hover:bg-slate-100 hover:text-slate-600"
                : "text-emerald-600 hover:bg-emerald-50",
            )}
          >
            {row.active ? <Archive className="h-3.5 w-3.5" /> : <RotateCcw className="h-3.5 w-3.5" />}
          </button>
        )}
      </div>
      </div>
      {showTuneups && <TuneupTemplateEditor row={row} canManage={canManage} />}
    </li>
  );
}

// ── Per-plan tune-up template — which checks this plan auto-creates on sale ──
function TuneupTemplateEditor({ row, canManage }: { row: PlanPrice; canManage: boolean }) {
  const save = useSavePlanPrice();
  const usingDefault = !row.tuneups || row.tuneups.length === 0;
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<TuneupTemplateItem[]>(() =>
    (row.tuneups && row.tuneups.length ? row.tuneups : DEFAULT_TUNEUP_TEMPLATE).map((t) => ({ ...t })),
  );
  const [flash, setFlash] = useState(false);

  // Re-seed from the server row when it changes (same stale-state fix as the
  // price grid) — but never while the editor is open mid-edit.
  useEffect(() => {
    if (!open) setItems((row.tuneups && row.tuneups.length ? row.tuneups : DEFAULT_TUNEUP_TEMPLATE).map((t) => ({ ...t })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(row.tuneups)]);

  const summary = (row.tuneups && row.tuneups.length ? row.tuneups : DEFAULT_TUNEUP_TEMPLATE)
    .map((t) => `${t.label} (${MONTHS[t.month] ?? "?"})`).join(", ");

  const setItem = (i: number, patch: Partial<TuneupTemplateItem>) =>
    setItems((arr) => arr.map((t, j) => (j === i ? { ...t, ...patch } : t)));
  const addItem = () => setItems((arr) => [...arr, { category: "heating", label: "Tune-up", month: 10 }]);
  const removeItem = (i: number) => setItems((arr) => arr.filter((_, j) => j !== i));

  const commit = (value: TuneupTemplateItem[] | null) =>
    save.mutate({ id: row.id, tuneups: value }, { onSuccess: () => { setFlash(true); window.setTimeout(() => setFlash(false), 2200); setOpen(false); } });

  return (
    <div className="ml-1 mt-1.5 border-l-2 border-slate-100 pl-2.5">
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} disabled={!canManage}
          className="group/tu flex items-center gap-1.5 text-left text-[11px] text-slate-500 hover:text-slate-700 disabled:cursor-default">
          <Wrench className="h-3 w-3 text-slate-400" />
          <span><span className="font-semibold">Tune-ups:</span> {summary}{usingDefault ? " · default" : ""}</span>
          {canManage && <ChevronRight className="h-3 w-3 text-slate-300 group-hover/tu:text-slate-500" />}
          {flash && <span className="rounded bg-emerald-50 px-1 text-[10px] font-semibold text-emerald-700">saved ✓</span>}
        </button>
      ) : (
        <div className="rounded-lg border border-hairline bg-slate-50/60 p-2.5">
          <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Tune-ups this plan creates each year</div>
          <div className="space-y-1.5">
            {items.map((t, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <input value={t.label} disabled={!canManage} onChange={(e) => setItem(i, { label: e.target.value })}
                  className="min-w-0 flex-1 rounded border border-hairline bg-white px-1.5 py-1 text-xs text-slate-800" placeholder="Furnace tune-up" />
                <select value={t.month} disabled={!canManage} onChange={(e) => setItem(i, { month: Number(e.target.value) })}
                  className="rounded border border-hairline bg-white px-1 py-1 text-xs text-slate-700">
                  {MONTHS.slice(1).map((m, j) => <option key={m} value={j + 1}>{m}</option>)}
                </select>
                <select value={t.category} disabled={!canManage} onChange={(e) => setItem(i, { category: e.target.value })}
                  className="rounded border border-hairline bg-white px-1 py-1 text-xs text-slate-700">
                  {TUNEUP_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                {canManage && <button type="button" onClick={() => removeItem(i)} className="rounded p-1 text-slate-300 hover:bg-slate-100 hover:text-red-500"><X className="h-3 w-3" /></button>}
              </div>
            ))}
            {items.length === 0 && <p className="py-1 text-[11px] text-slate-400">No checks — this plan won't auto-create tune-ups.</p>}
          </div>
          {canManage && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <button type="button" onClick={addItem} className="inline-flex items-center gap-1 rounded border border-dashed border-slate-300 px-1.5 py-1 text-[11px] font-medium text-slate-500 hover:bg-white"><Plus className="h-3 w-3" /> Add a check</button>
              <button type="button" disabled={save.isPending} onClick={() => commit(items)} className="rounded bg-brand-700 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-brand-800 disabled:bg-brand-300">Save</button>
              <button type="button" onClick={() => commit(null)} title="Use the standard furnace-fall + A/C-spring schedule" className="text-[11px] font-medium text-slate-400 hover:text-slate-600">Reset to default</button>
              <button type="button" onClick={() => { setItems((row.tuneups && row.tuneups.length ? row.tuneups : DEFAULT_TUNEUP_TEMPLATE).map((t) => ({ ...t }))); setOpen(false); }} className="ml-auto text-[11px] font-medium text-slate-400 hover:text-slate-600">Cancel</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Repair discount: edited elsewhere, surfaced here for context ────────────
function RepairDiscountNote() {
  const { data: tiers } = usePlanDiscounts();
  return (
    <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/60 p-4">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
        Member repair discount
        <InfoTip label="Where the repair discount is set">
          It's set once in Service Fees → Member Repair Discount, and the plan builder shows it by term, so there's no
          separate price to keep in sync here.
        </InfoTip>
      </div>
      {tiers && tiers.length > 0 && (
        <p className="mt-1 text-[11px] leading-relaxed text-slate-500">
          Currently:{" "}
          {tiers
            .slice()
            .sort((a, b) => Number(a.min_years) - Number(b.min_years))
            .map((t) => `${t.repair_pct}% (${t.label})`)
            .join(" · ")}
        </p>
      )}
    </div>
  );
}
